import { Download } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import * as React from 'react';

import { useLegalClient } from '@/api/legal-client-context';
import { Button } from '@/components/ui/button';

// =============================================================================
// cgu-pdf-download.tsx — Téléchargement du PDF horodaté des CGU AVANT signature (DBU §3.1).
// =============================================================================
// DBU §3.1 « Écran de signature > Option de téléchargement du PDF horodaté de la version proposée,
// accessible AVANT validation (le contact peut télécharger le document avant de signer pour archivage
// personnel) ». Le back est INCHANGÉ : on consomme l'endpoint DÉJÀ livré GET /legal/cgu/current/pdf
// (LegalClient.currentCguPdf → octets application/pdf). Aucune donnée métier en localStorage.
//
// L'action télécharge les OCTETS à la demande (mutation) puis déclenche un enregistrement navigateur
// (Blob + ancre + objectURL révoqué). Le sha256 figé sur la version (réf) reste l'ancrage probatoire ;
// ces octets en sont l'intégrité matérialisée (parité renderer pdf.js). Aucune valeur juridique posée
// ici : télécharger n'est PAS signer (contentDisplayed reste géré par le scroll-gate du cgu-screen).
//
// WCAG 2.1 AA : <button> étiqueté (accessible name explicite), icône décorative aria-hidden, état de
// chargement doublé d'un LIBELLÉ TEXTE, erreur annoncée (role="alert").

/** Déclenche l'enregistrement navigateur des octets PDF sous `filename`. */
function savePdf(bytes: ArrayBuffer, filename: string): void {
  const blob = new Blob([bytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export interface CguPdfDownloadButtonProps {
  /** Numéro de version lisible (format `CGU-AAAA-MM`) — construit le nom du fichier téléchargé. */
  readonly versionNumber: string;
}

export function CguPdfDownloadButton({
  versionNumber,
}: CguPdfDownloadButtonProps): React.JSX.Element {
  const client = useLegalClient();
  const filename = `${versionNumber || 'CGU'}.pdf`;

  const download = useMutation<ArrayBuffer, Error, void>({
    mutationKey: ['legal', 'current-cgu-pdf-download', versionNumber],
    mutationFn: () => client.currentCguPdf(),
    onSuccess: (bytes) => {
      savePdf(bytes, filename);
    },
  });

  return (
    <div className="flex flex-col gap-2">
      <Button
        type="button"
        variant="outline"
        onClick={() => download.mutate()}
        disabled={download.isPending}
      >
        <Download className="mr-2 h-4 w-4" aria-hidden="true" />
        {download.isPending
          ? 'Téléchargement du PDF…'
          : 'Télécharger le PDF des conditions'}
      </Button>
      {download.isError ? (
        <p role="alert" className="text-sm text-destructive">
          Le téléchargement du PDF a échoué : {download.error.message}
        </p>
      ) : null}
    </div>
  );
}
